import { sidebarConfig, useSidebarOpenState } from '@backstage/core-components';
import { Focusable, Tooltip, TooltipTrigger } from '@backstage/ui';
import { RiArrowLeftSLine, RiArrowRightSLine } from '@remixicon/react';
import {
  compactAriaShortcuts,
  compactShortcutText,
  isMacPlatform,
  useCompactNav,
  useCompactShortcut,
  useSidebarPinSync,
} from './compactNav';

export const COLLAPSE_LABEL = 'Réduire le menu';
export const EXPAND_LABEL = 'Déplier le menu';

/** The tab's height; its top centres it on the logo. */
const TAB_HEIGHT = 40;

/**
 * The compact menu's state applied to the sidebar (`useSidebarPinSync`) and
 * its `[` and Ctrl+B shortcuts. Renders nothing; a child of the Sidebar, so
 * that the phone's bottom bar — core-components' MobileSidebar, which keeps
 * the SidebarGroups alone — never mounts it.
 */
export function CompactNavController() {
  const { compact, toggle } = useCompactNav();
  useSidebarPinSync(compact);
  useCompactShortcut(toggle);
  return null;
}

/**
 * The tab on the menu's right edge (mockup « A2 », 2026-10-29), folding it
 * into the compact rail and back: « ‹ » while expanded, « › » in the rail,
 * level with the logo in both. A disclosure button: `aria-expanded` says
 * whether the menu is expanded, the name says what a press does; the
 * tooltip adds the shortcut.
 *
 * `position: fixed` (styles.css `.waadoo-nav-tab`): the drawer clips its
 * overflow (core-components' `overflowX: hidden`, Bar.esm.js), which a
 * fixed box escapes, the drawer and its root having no transform; and the
 * tab stays put while the menu's list scrolls. Inside the sidebar's root,
 * the tab shares its stacking level (`zIndex.appBar`), over the page.
 * Not a SidebarGroup: the phone's bottom bar leaves it out.
 */
export function SidebarCompactToggle() {
  const { toggle } = useCompactNav();
  // Drawn from the drawer's state, as every other entry is: the two agree
  // from the first paint on (`useSidebarPinSync`).
  const { isOpen } = useSidebarOpenState();
  const mac = isMacPlatform(window.navigator.platform);
  const label = isOpen ? COLLAPSE_LABEL : EXPAND_LABEL;
  const Arrow = isOpen ? RiArrowLeftSLine : RiArrowRightSLine;
  const width = isOpen
    ? sidebarConfig.drawerWidthOpen
    : sidebarConfig.drawerWidthClosed;

  return (
    <TooltipTrigger>
      <Focusable>
        <button
          type="button"
          className="waadoo-nav-tab"
          // Over the drawer's 1 px right border (themes.ts), so that the
          // tab reads as the menu's surface carried on.
          style={{
            left: width - 1,
            // The logo's row is 3 × logoHeight tall (SidebarLogo.tsx), the
            // first of the drawer.
            top: (3 * sidebarConfig.logoHeight - TAB_HEIGHT) / 2,
          }}
          aria-label={label}
          aria-expanded={isOpen}
          aria-keyshortcuts={compactAriaShortcuts(mac)}
          onClick={toggle}
        >
          <Arrow aria-hidden focusable="false" />
        </button>
      </Focusable>
      <Tooltip placement="right">
        {label}
        <kbd className="waadoo-nav-tab__kbd">{compactShortcutText(mac)}</kbd>
      </Tooltip>
    </TooltipTrigger>
  );
}
